import { conceptOf, daysBetween, observationValue } from './context.js';
import { parse, test } from './expr.js';
import { EXT_CDS_RULE, EXT_EPISODE_END_REASON, EXT_FOLLOW_UP_FROM, EXT_REFERRAL_OUTCOME, SYS_PROGRAMME, SYS_SNOMED, SYS_TASK, SYS_TRACING_OUTCOME, patientIdOf, } from './fhir.js';
import { newId } from './ids.js';
import { liveResources } from './removal.js';
import { addDays, nextVisitOf, splitRecord } from './visit.js';
export const DEFAULT_FOLLOWUP = {
    default_return_days: 28,
    ltfu_days: 365,
    missed_visit_days: 90,
};
const extsOf = (r) => (r.extension ?? []);
const dateOf = (dt) => dt.slice(0, 10);
export const isDeceased = (p) => !!p.deceasedBoolean ||
    !!p.deceasedDateTime;
const hasProgramme = (types, code) => (types ?? []).some((t) => t.coding?.some((c) => c.system === SYS_PROGRAMME && c.code === code));
export function activeEpisode(record, programme) {
    return record.episodes.find((e) => e.status === 'active' && hasProgramme(e.type, programme));
}
/** Programme visits, newest first (cancelled or erroneous ones excluded). */
export function programmeVisits(record, programme) {
    return record.encounters
        .filter((e) => e.status !== 'cancelled' &&
        e.status !== 'entered-in-error' &&
        hasProgramme(e.type, programme))
        .sort((a, b) => b.period.start.localeCompare(a.period.start));
}
export const TRACING_OUTCOMES = [
    'will_return',
    'no_answer',
    'unreachable',
    'wrong_number',
    'refused',
    'transferred',
    'died',
];
/** A refusal takes the patient off the tracing list for this long (they still count as registered). */
export const REFUSED_PAUSE_DAYS = 90;
/** "Will come back" without a date: back on the list after a week. */
export const PROMISE_DEFAULT_DAYS = 7;
export const isTracingTask = (t) => t.status !== 'entered-in-error' &&
    !!t.code.coding?.some((c) => c.system === SYS_TASK && c.code === 'tracing-attempt');
export function tracingAttempts(tasks) {
    return tasks
        .filter(isTracingTask)
        .map((t) => ({
        id: t.id,
        date: t.authoredOn,
        method: t.input?.[0]?.valueCode ??
            null,
        outcome: t.businessStatus?.coding?.find((c) => c.system === SYS_TRACING_OUTCOME)
            ?.code ?? null,
        followUpFrom: extsOf(t).find((x) => x.url === EXT_FOLLOW_UP_FROM)?.valueDate ?? null,
        note: t.note?.[0]?.text ?? null,
    }))
        .sort((a, b) => b.date.localeCompare(a.date));
}
/**
 * Resources recording one tracing attempt. 'died' also marks the patient deceased; 'died' and
 * 'transferred' end their programme enrolments, so they leave worklists and indicator cohorts.
 */
export function tracingResources(v) {
    const makeId = v.makeId ?? newId;
    let followUpFrom = null;
    if (v.outcome === 'will_return')
        followUpFrom =
            v.returnDate && v.returnDate > v.today
                ? v.returnDate
                : addDays(v.today, PROMISE_DEFAULT_DAYS);
    if (v.outcome === 'refused')
        followUpFrom = addDays(v.today, REFUSED_PAUSE_DAYS);
    const task = {
        resourceType: 'Task',
        id: makeId(),
        status: 'completed',
        intent: 'order',
        code: { coding: [{ system: SYS_TASK, code: 'tracing-attempt', display: 'Tracing attempt' }] },
        for: { reference: `Patient/${v.patient.id}` },
        authoredOn: v.nowIso,
        businessStatus: { coding: [{ system: SYS_TRACING_OUTCOME, code: v.outcome }] },
        ...(v.note?.trim() ? { note: [{ text: v.note.trim() }] } : {}),
    };
    const t = task;
    t.input = [{ type: { text: 'method' }, valueCode: v.method }];
    if (followUpFrom)
        t.extension = [{ url: EXT_FOLLOW_UP_FROM, valueDate: followUpFrom }];
    const out = [task];
    if (v.outcome === 'died' || v.outcome === 'transferred') {
        for (const e of v.record.episodes) {
            if (e.status !== 'active')
                continue;
            out.push({
                ...e,
                status: 'finished',
                period: { ...(e.period ?? { start: v.today }), end: v.today },
                extension: [
                    ...extsOf(e).filter((x) => x.url !== EXT_EPISODE_END_REASON),
                    {
                        url: EXT_EPISODE_END_REASON,
                        valueCode: v.outcome === 'died' ? 'died' : 'transferred-out',
                    },
                ],
            });
        }
    }
    if (v.outcome === 'died')
        out.push({ ...v.patient, deceasedBoolean: true });
    return out;
}
/** Everyone with an episode programme open (women in labour), most recent first. */
export function openEpisodes(bundles, programmes) {
    const out = [];
    for (const b of bundles)
        for (const p of programmes) {
            if (!p.episode)
                continue;
            const episode = activeEpisode(b.record, p.code);
            if (episode)
                out.push({ patient: b.patient, episode, programme: p });
        }
    return out.sort((a, b) => (b.episode.period?.start ?? '').localeCompare(a.episode.period?.start ?? ''));
}
export function followupStatus(record, programme, today) {
    if (programme.episode)
        return null; // an episode (a labour) has no return date to trace
    const ep = activeEpisode(record, programme.code);
    if (!ep)
        return null;
    const cfg = programme.followup ?? DEFAULT_FOLLOWUP;
    const enrolledOn = dateOf(ep.period?.start ?? today);
    const last = programmeVisits(record, programme.code)[0];
    const lastVisit = last ? dateOf(last.period.start) : null;
    const set = last ? nextVisitOf(last) : null;
    const base = lastVisit ?? enrolledOn;
    const dueDate = set ?? addDays(base, cfg.default_return_days);
    const daysLate = daysBetween(dueDate, today);
    const since = last?.period.start ?? '';
    const attempts = tracingAttempts(record.tasks).filter((a) => a.date > since);
    const pausedUntil = attempts.find((a) => a.followUpFrom)?.followUpFrom ?? null;
    let state;
    if (daysBetween(base, today) >= cfg.ltfu_days)
        state = 'lost';
    else if (daysLate < 0)
        state = 'upcoming';
    else if (daysLate === 0)
        state = 'due';
    else if (pausedUntil && pausedUntil > today)
        state = 'promised';
    else
        state = 'overdue';
    return {
        programme: programme.code,
        enrolledOn,
        lastVisit,
        dueDate,
        dueDateSet: !!set,
        daysLate,
        state,
        attempts,
        pausedUntil,
    };
}
const STATE_ORDER = ['lost', 'overdue', 'due', 'promised', 'upcoming'];
/** One entry per patient: their most pressing programme follow-up. */
export function worklist(bundles, packs, today) {
    const programmes = packs.flatMap((p) => p.programmes);
    const out = [];
    for (const b of bundles) {
        if (isDeceased(b.patient))
            continue;
        const statuses = programmes
            .map((p) => followupStatus(b.record, p, today))
            .filter((s) => !!s)
            .sort((a, c) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(c.state) || c.daysLate - a.daysLate);
        const first = statuses[0];
        if (first)
            out.push({ patient: b.patient, status: first, programmes: statuses.map((s) => s.programme) });
    }
    return out.sort((a, b) => b.status.daysLate - a.status.daysLate);
}
const astCache = new Map();
export function computeIndicator(def, bundles, today) {
    const minDays = def.registered_before_days ?? 0;
    const cohort = bundles.filter((b) => {
        if (isDeceased(b.patient))
            return false;
        const ep = activeEpisode(b.record, def.programme);
        return !!ep && daysBetween(ep.period?.start ?? today, today) >= minDays;
    });
    const window = def.window_days ?? 0;
    const inWindow = (dt) => {
        const d = daysBetween(dt, today);
        return d >= 0 && d < window;
    };
    let numerator = cohort.length;
    if (def.kind === 'no_visit_within') {
        numerator = cohort.filter((b) => !programmeVisits(b.record, def.programme).some((e) => inWindow(e.period.start))).length;
    }
    else if (def.kind === 'controlled') {
        let ast = astCache.get(def.condition ?? '');
        if (!ast) {
            ast = parse(def.condition ?? 'false');
            astCache.set(def.condition ?? '', ast);
        }
        numerator = cohort.filter((b) => {
            const ctx = {};
            for (const c of def.concepts ?? []) {
                const latest = b.record.observations
                    .filter((o) => o.status !== 'entered-in-error' &&
                    o.status !== 'cancelled' &&
                    conceptOf(o) === c &&
                    inWindow(o.effectiveDateTime))
                    .sort((x, y) => y.effectiveDateTime.localeCompare(x.effectiveDateTime))[0];
                ctx[c] = latest ? observationValue(latest) : null;
            }
            return test(ast, ctx);
        }).length;
    }
    const isCount = def.kind === 'registered';
    return {
        id: def.id,
        title: def.title,
        programme: def.programme,
        kind: def.kind,
        numerator,
        denominator: cohort.length,
        percent: isCount || cohort.length === 0 ? null : Math.round((numerator / cohort.length) * 1000) / 10,
        higherIsBetter: def.higher_is_better ?? null,
    };
}
/** The facility's patients with their records, without anything removed as entered in error. */
export function bundlesOf(resources) {
    const patients = new Map();
    const byPatient = new Map();
    for (const r of liveResources(resources)) {
        if (r.resourceType === 'Patient') {
            patients.set(r.id, r);
            continue;
        }
        const pid = patientIdOf(r);
        const list = byPatient.get(pid);
        if (list)
            list.push(r);
        else
            byPatient.set(pid, [r]);
    }
    return [...patients.values()].map((patient) => ({
        patient,
        record: splitRecord(byPatient.get(patient.id) ?? []),
    }));
}
/**
 * Hours since anything was recorded in an open episode (its last visit, else its start). Past the
 * programme's `review_after_hours` the episode is flagged on its list: is she still here?
 */
export function quietHours(episode, encounters, nowIso) {
    const start = episode.period?.start ?? '';
    const last = encounters
        .map((e) => e.period.start)
        .filter((d) => d >= start)
        .sort()
        .pop();
    const since = last ?? start;
    if (!since)
        return 0;
    return Math.max(0, Math.floor((Date.parse(nowIso) - Date.parse(since)) / 3_600_000));
}
//# sourceMappingURL=followup.js.map